/**
 * The task descriptor — "what is the user looking at" — for assistant requests.
 *
 * See docs/TASK_CONTEXT_REGISTRY.md (§3 vocabulary, §5 surface matrix, §7.2 the run boundary).
 *
 * WHAT THIS IS. When a user opens a bank transaction and asks "vad är det här?", the agent today
 * starts blind: it spends two or three tool round-trips rediscovering the row the SPA already had
 * on screen. This module is the SPA's half of closing that gap — a typed
 * `{ surface, entityType, entityId, intent }` descriptor, derived from code-owned selection
 * state, carried to the assistant and sent alongside the message.
 *
 * WHAT THIS IS NOT. It is a HINT, never a capability. The gateway validates it against the closed
 * registry, the backend independently verifies the user's delegation and company access, and a
 * tenant database is per-company so an entity id from another tenant simply is not in it. Nothing
 * a client puts in this object authorizes anything, and nothing here should ever be written as
 * though it did.
 *
 * DERIVED FROM STATE, NEVER FROM TEXT. The descriptor is built from the selection state the page
 * already owns (`selectedLine.id`), not from anything rendered on screen and not from the user's
 * message. Reading it out of page text would make an attacker-controlled bank descriptor an input
 * to which row the agent reads.
 *
 * LIFECYCLE, which is the reason this is a store and not a prop:
 *   - a surface DECLARES its descriptor while it is mounted and something is selected;
 *   - selecting a different row REPLACES it — there is only ever one;
 *   - leaving the surface CLEARS it, so a stale entity cannot follow the user to another page.
 *
 * The assistant is a separate route, so the bank page unmounts (and clears) before the chat page
 * mounts. The hand-off is therefore explicit — the router-state descriptor the chat page adopts
 * (see `readHandOff`) — rather than an implicit read of a store the navigation just emptied. The
 * live store still matters: it is what makes replacement and clearing observable and testable,
 * and it is what an in-page assistant would read.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// The closed vocabulary — mirrored from the backend's contract artifact
// ---------------------------------------------------------------------------

/**
 * Product surfaces (NOT the transport channel). Mirrors
 * `task-context-contracts.json#vocabulary.productSurfaces`.
 *
 * Mirrored rather than fetched: this is a handful of code-owned string constants that change only
 * when a surface ships, and making the assistant entry point depend on a network read would mean
 * a descriptor silently stops being sent whenever that read fails. The gateway re-validates
 * everything against the real artifact, so a stale mirror costs a refused descriptor — a logged,
 * visible outcome — and never a wrong one.
 */
export const PRODUCT_SURFACES = Object.freeze([
    'bank.transaction.detail',
    'supplier-invoice.detail',
    'reconciliation.queue.item',
    'payroll.run.detail',
    'customer-invoice.detail',
    'year-end.step',
    'vat.account.review',
    'unknown',
]);

export const TASK_ENTITY_TYPES = Object.freeze([
    'bankstatementline',
    'supplier_invoice',
    'matching_queue_item',
    'payroll',
    'customer_invoice',
    'year_end_process',
    'vat_account_review',
    'none',
]);

/**
 * The intents each surface ACCEPTS — its default plus its secondaries. Mirrors
 * `task-context-contracts.json#surfaceIntents`. The first entry is the surface's default.
 *
 * Three individually-valid values can still name a combination no contract covers, so the tuple
 * is validated as a whole here exactly as the gateway validates it.
 */
export const SURFACE_INTENTS = Object.freeze({
    'bank.transaction.detail': Object.freeze(['transaction.categorize', 'transaction.explain']),
    'supplier-invoice.detail': Object.freeze(['ap.invoice.review', 'ap.invoice.book']),
    'reconciliation.queue.item': Object.freeze(['reconciliation.match.review']),
    'payroll.run.detail': Object.freeze(['payroll.run.review']),
    'customer-invoice.detail': Object.freeze(['ar.invoice.review', 'ar.invoice.chase']),
    'year-end.step': Object.freeze(['yearend.step.advise']),
    'vat.account.review': Object.freeze(['vat.account.explain']),
    unknown: Object.freeze(['general.chat']),
});

/**
 * The entity type each surface addresses — the §5 surface matrix, one row per surface.
 *
 * Checked as a PAIR rather than as two independent memberships: `bank.transaction.detail` with
 * `entityType: "payroll"` names two real vocabulary members and a combination no contract covers,
 * and sending it would cost a round-trip and a refusal.
 */
export const SURFACE_ENTITY_TYPES = Object.freeze({
    'bank.transaction.detail': 'bankstatementline',
    'supplier-invoice.detail': 'supplier_invoice',
    'reconciliation.queue.item': 'bankstatementline',
    'payroll.run.detail': 'payroll',
    'customer-invoice.detail': 'customer_invoice',
    'year-end.step': 'year_end_process',
    'vat.account.review': 'vat_account_review',
    unknown: 'none',
});

/**
 * What a well-formed entity id looks like, per entity type. Mirrors
 * `task-context-contracts.json#entityIdGrammars`.
 *
 * Shape only. It says an id could name a row of this kind; it never says the row exists, belongs
 * to this tenant, or may be read — those are the backend's answers and no grammar substitutes for
 * either. Checking it here keeps a malformed id from costing a round-trip and a refusal.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ENTITY_ID_GRAMMARS = Object.freeze({
    vat_account_review: Object.freeze({ pattern: /^(year|period):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:[1-9][0-9]{0,9}$/i, maxChars: 54 }),
    bankstatementline: Object.freeze({ pattern: UUID, maxChars: 36 }),
    none: Object.freeze({ pattern: /^$/, maxChars: 0 }),
});
const DEFAULT_ENTITY_ID_GRAMMAR = Object.freeze({
    pattern: /^[a-z0-9][a-z0-9._:@-]{0,79}$/i,
    maxChars: 64,
});

function entityIdGrammarFor(entityType) {
    return ENTITY_ID_GRAMMARS[entityType] || DEFAULT_ENTITY_ID_GRAMMAR;
}

/** The surface's default intent — what the registry resolves when none is named. */
export function defaultIntentFor(surface) {
    return (SURFACE_INTENTS[surface] || [])[0];
}

/** Identity only: the authorized backend re-reads the period and its evidence at send time. */
export function vatAccountReviewDescriptor({ accountId, accountNumber, fiscalYearId, vatPeriodId }) {
    const descriptor = { surface: 'vat.account.review', entityType: 'vat_account_review', intent: 'vat.account.explain',
        entityId: `${vatPeriodId ? 'period' : 'year'}:${vatPeriodId || fiscalYearId}:${accountId}`,
        label: `Momsbehandling — konto ${accountNumber}` };
    return isValidTaskDescriptor(descriptor) ? Object.freeze(descriptor) : null;
}

export function editableTaskQuestion(locationState) {
    return readHandOff(locationState) && typeof locationState?.prefillMessage === 'string'
        ? locationState.prefillMessage.slice(0, 1200) : '';
}

export function taskReturnPath(locationState) {
    const path = locationState?.taskReturnPath;
    return readHandOff(locationState) && typeof path === 'string' && /^\/(?!\/)[^\r\n\\]*$/.test(path) ? path : null;
}

/**
 * Is this a descriptor the registry serves?
 *
 * The same four checks the gateway makes, in the same order: shape, vocabulary, the COMPLETE
 * tuple, and the entity id's grammar. Duplicated deliberately — the gateway's copy is the one
 * that matters for safety, and this one exists so an obviously-broken descriptor is never sent
 * and a page-level bug shows up in a unit test rather than as a refusal in a production log.
 */
export function isValidTaskDescriptor(descriptor) {
    if (!descriptor || typeof descriptor !== 'object') return false;
    const { surface, entityType, entityId, intent } = descriptor;
    if (typeof surface !== 'string' || !PRODUCT_SURFACES.includes(surface)) return false;
    if (typeof entityType !== 'string' || !TASK_ENTITY_TYPES.includes(entityType)) return false;
    if (SURFACE_ENTITY_TYPES[surface] !== entityType) return false;
    if (typeof entityId !== 'string') return false;

    const resolved = intent === undefined ? defaultIntentFor(surface) : intent;
    if (typeof resolved !== 'string') return false;
    if (!(SURFACE_INTENTS[surface] || []).includes(resolved)) return false;

    const grammar = entityIdGrammarFor(entityType);
    if (entityId.length > grammar.maxChars) return false;
    return grammar.pattern.test(entityId);
}

/**
 * Strip a descriptor down to exactly the four wire fields, frozen.
 *
 * Anything else a caller attached — a display label, a React key, a whole row object — is dropped
 * here rather than sent. The descriptor crosses a trust boundary, and "only these fields leave"
 * should be a property of one function and not of every call site's discipline.
 */
export function toWireDescriptor(descriptor) {
    if (!isValidTaskDescriptor(descriptor)) return null;
    const intent = descriptor.intent || defaultIntentFor(descriptor.surface);
    return Object.freeze({
        surface: descriptor.surface,
        entityType: descriptor.entityType,
        entityId: descriptor.entityId,
        intent,
    });
}

// ---------------------------------------------------------------------------
// Builders — one per surface, so "which row" is derived in exactly one place
// ---------------------------------------------------------------------------

/**
 * The bank-transaction descriptor (S1), from a selected `bankstatementline` row.
 *
 * Takes the ROW, not an id, so the label travels with it: showing the user which transaction the
 * assistant is about to look at needs a human-readable summary, and deriving that here keeps the
 * chat page from having to know a bank row's shape.
 *
 * `label` is DISPLAY ONLY and is never sent — `toWireDescriptor` drops it. That matters: a bank
 * descriptor is attacker-controlled text, and the one thing it must never become is an input to
 * which row gets read.
 */
export function bankTransactionDescriptor(line, options = {}) {
    if (!line || typeof line.id !== 'string') return null;
    const descriptor = {
        surface: 'bank.transaction.detail',
        entityType: 'bankstatementline',
        entityId: line.id,
        intent: options.intent || 'transaction.explain',
    };
    if (!isValidTaskDescriptor(descriptor)) return null;
    return Object.freeze({ ...descriptor, label: bankTransactionLabel(line) });
}

/** One bank row viewed in the needs-attention reconciliation queue (S3). */
export function reconciliationQueueItemDescriptor(line, options = {}) {
    if (!line || typeof line.id !== 'string') return null;
    const descriptor = {
        surface: 'reconciliation.queue.item',
        entityType: 'bankstatementline',
        entityId: line.id,
        intent: options.intent || 'reconciliation.match.review',
    };
    if (!isValidTaskDescriptor(descriptor)) return null;
    return Object.freeze({ ...descriptor, label: bankTransactionLabel(line) });
}

/** Customer-invoice detail descriptor (S5). The number is display-only and never sent. */
export function customerInvoiceDescriptor(invoice, options = {}) {
    if (!invoice || typeof invoice.invoice_id !== 'string') return null;
    const descriptor = {
        surface: 'customer-invoice.detail',
        entityType: 'customer_invoice',
        entityId: invoice.invoice_id,
        intent: options.intent || 'ar.invoice.review',
    };
    if (!isValidTaskDescriptor(descriptor)) return null;
    const number = String(invoice.invoice_number || '').trim();
    const label = number ? `Faktura ${number}` : 'Kundfaktura';
    return Object.freeze({ ...descriptor, label });
}

/**
 * Payroll-run descriptor (S4). Only the run id is sent. The descriptor carries NO
 * authorization claim: the backend resolves payroll access for the CURRENT reader
 * from catalog roles and answers an unauthorized reader with an empty capture,
 * whoever declared the descriptor.
 */
export function payrollRunDescriptor(payroll, options = {}) {
    if (!payroll || typeof payroll.id !== 'string') return null;
    const descriptor = {
        surface: 'payroll.run.detail',
        entityType: 'payroll',
        entityId: payroll.id,
        intent: options.intent || 'payroll.run.review',
    };
    if (!isValidTaskDescriptor(descriptor)) return null;
    const year = Number(payroll.year);
    const month = Number(payroll.month);
    const label = Number.isInteger(year) && Number.isInteger(month) && month >= 1 && month <= 12
        ? `Lönekörning ${year}-${String(month).padStart(2, '0')}`
        : 'Lönekörning';
    return Object.freeze({ ...descriptor, label });
}

/** Supplier-invoice detail descriptor (S2). Supplier text is display-only and never sent. */
export function supplierInvoiceDescriptor(invoice, options = {}) {
    if (!invoice || typeof invoice.invoice_id !== 'string') return null;
    const descriptor = {
        surface: 'supplier-invoice.detail',
        entityType: 'supplier_invoice',
        entityId: invoice.invoice_id,
        intent: options.intent || 'ap.invoice.review',
    };
    if (!isValidTaskDescriptor(descriptor)) return null;
    const number = String(invoice.invoice_number || '').trim();
    const supplier = String(invoice.supplier_name || '').trim();
    const label = [number && `Faktura ${number}`, supplier].filter(Boolean).join(' · ') || 'Leverantörsfaktura';
    return Object.freeze({ ...descriptor, label });
}

/**
 * A short human summary of the row — date, amount, text — for the "what am I looking at" chip.
 *
 * The TEXT half prefers `reference` over `description`, because on a Swedish bank feed the
 * description is usually the transaction TYPE ("Kortköp/uttag", "Autogiro") — the same handful of
 * strings on every row — while the reference carries what actually identifies it: the merchant,
 * the Swish message, the OCR number. A chip that says "Kortköp/uttag" tells the user nothing
 * about WHICH card purchase they are asking about, which is the one thing the chip exists for.
 *
 * Both when they differ and neither is redundant: "SWISH Kaffekvarnen · Kortköp/uttag" reads
 * better than either alone, and the type still disambiguates two same-merchant rows.
 *
 * Display only. The label never reaches the gateway — `toHandOffState` sends the four wire
 * fields and drops this — so it is free to be as human as it likes. It is also EXTERNAL text
 * (written by a counterparty), which is safe here because React escapes it; it must never be
 * treated as anything but a caption.
 */
export function bankTransactionLabel(line) {
    const parts = [];
    const date = typeof line.transaction_date === 'string' ? line.transaction_date.slice(0, 10) : null;
    if (date) parts.push(date);
    const amount = Number.parseFloat(line.amount);
    if (Number.isFinite(amount)) {
        parts.push(`${amount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`);
    }
    const reference = (line.reference || '').trim();
    const description = (line.description || '').trim();
    const text = [reference, description !== reference ? description : ''].filter(Boolean).join(' · ');
    if (text) parts.push(text.length > 60 ? `${text.slice(0, 60)}…` : text);
    return parts.join(' · ') || 'Bankhändelse';
}

// ---------------------------------------------------------------------------
// The live store
// ---------------------------------------------------------------------------

const TaskDescriptorContext = createContext({
    descriptor: null,
    declare: () => {},
    clear: () => {},
});

export function TaskDescriptorProvider({ children }) {
    const [descriptor, setDescriptor] = useState(null);

    // Identity-stable across renders so `useSurfaceTaskDescriptor`'s effect can depend on them
    // without re-firing every render — which would clear and re-declare on each keystroke.
    const declare = useCallback((next) => {
        setDescriptor(next && isValidTaskDescriptor(next) ? Object.freeze({ ...next }) : null);
    }, []);
    const clear = useCallback(() => setDescriptor(null), []);

    const value = useMemo(() => ({ descriptor, declare, clear }), [descriptor, declare, clear]);
    return <TaskDescriptorContext.Provider value={value}>{children}</TaskDescriptorContext.Provider>;
}

/** Read the live descriptor and the two mutators. */
export function useTaskDescriptor() {
    return useContext(TaskDescriptorContext);
}

/**
 * Declare THIS surface's descriptor for as long as it is selected.
 *
 * Replaces on change, clears on unmount, and does nothing at all when the argument is null — the
 * three lifecycle rules in one hook, so no surface has to re-implement them and none can
 * implement them differently.
 *
 * Keyed on the descriptor's WIRE identity rather than object identity: a page that rebuilds its
 * row objects on every poll would otherwise re-declare continuously, and a page that mutates a
 * row in place would never re-declare at all.
 */
export function useSurfaceTaskDescriptor(descriptor) {
    const { declare, clear } = useTaskDescriptor();
    const wire = descriptor ? toWireDescriptor(descriptor) : null;
    const key = wire ? `${wire.surface}|${wire.entityType}|${wire.entityId}|${wire.intent}` : null;
    // The label rides along, so a changed label alone does not count as a changed selection.
    const latest = useRef(descriptor);
    latest.current = descriptor;

    useEffect(() => {
        if (!key) {
            clear();
            return undefined;
        }
        declare(latest.current);
        // Clearing on unmount is what stops a stale entity following the user to another page —
        // and it is why the assistant hand-off is an explicit router-state payload rather than a
        // read of this store: on navigation, this cleanup runs before the next route mounts.
        return () => clear();
    }, [key, declare, clear]);
}

// ---------------------------------------------------------------------------
// The hand-off to the assistant route
// ---------------------------------------------------------------------------

/** The router-state key the bank page writes and the assistant page adopts. */
export const HAND_OFF_STATE_KEY = 'taskContext';

/**
 * Build the router state for "ask the assistant about this".
 *
 * The label travels beside the descriptor rather than inside it, so the wire object stays exactly
 * the four typed fields and the chip still has something to show.
 */
export function handOffState(descriptor, extra = {}) {
    const wire = toWireDescriptor(descriptor);
    if (!wire) return { ...extra };
    return { ...extra, [HAND_OFF_STATE_KEY]: { ...wire, label: descriptor.label || null } };
}

/**
 * Read a hand-off out of router state, re-validating it.
 *
 * Re-validated because router state is not a trusted channel — it survives history navigation and
 * can be written by anything holding the history object — and because the cost of validating is a
 * regex. A hand-off that does not check out is simply absent, which is an ordinary entity-less
 * assistant session.
 */
export function readHandOff(locationState) {
    const raw = locationState && locationState[HAND_OFF_STATE_KEY];
    const wire = toWireDescriptor(raw);
    if (!wire) return null;
    return Object.freeze({ ...wire, label: typeof raw.label === 'string' ? raw.label : null });
}

export default TaskDescriptorContext;
